'use client'

import type { PropsWithChildren, ReactNode } from 'react'
import * as RadixTooltip from '@radix-ui/react-tooltip'

import { themeBackgroundBlurMap } from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'

type TooltipVariant = 'default' | 'inverse'

type TooltipProps = PropsWithChildren<{
  className?: string
  content: ReactNode
  disabled?: boolean
  delay?: number
  variant?: TooltipVariant
}>

const variantClassMap: Record<
  TooltipVariant,
  { content: string; arrow: string }
> = {
  default: {
    content: cn('bg-neutral-900 text-primary'),
    arrow: 'fill-neutral-900',
  },
  inverse: {
    content: cn('bg-neutral-100 text-primary-inverse'),
    arrow: 'fill-neutral-100',
  },
}

const zIndex = 'z-50'

const Tooltip = ({
  className,
  children,
  content,
  disabled,
  delay,
  variant = 'default',
}: TooltipProps) => {
  const classes = variantClassMap[variant]

  if (!content || disabled) {
    return children
  }

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root delayDuration={delay}>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>

        <RadixTooltip.Portal>
          <RadixTooltip.Content
            className={cn(
              'rounded-lg p-3 text-xs leading-normal animate-drop-fade max-w-80',
              zIndex,
              themeBackgroundBlurMap['blur-sm'],
              classes.content,
              className
            )}
            sideOffset={6}
          >
            {content}
            <RadixTooltip.Arrow
              width="16"
              height="8"
              className={cn(
                zIndex,
                themeBackgroundBlurMap['blur-sm'],
                classes.arrow
              )}
            />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}

export { Tooltip }
